import { ApplyInputResultPool } from "./apply_input_result_pool"
import { collisionMover, Coordinate, Entity, TileSize, WeaponCharacteristics } from "./models"
import { TileMapMetaProvider } from "./tile_map_meta_provider"
import { cliffLevelsOf } from "./cliff_mover"
import { ShotAim, reachPoint, shotCliffMover, shotFaceStrike } from "./shot_cliff"

export { reachPoint }

/**
 * HOW A SPENT ARROW COMES DOWN (openspec/specs/combat/spent-arrows), as both sides work it out.
 *
 * The server decides what is left (server/src/combat/spent_arrows.ts) and every client draws what it is
 * told. The shooter's client also draws its own arrow's landing the moment the flight ends, a round trip
 * before the server's word arrives (client/src/predicted_landings.ts), so the pose, whether the arrow lasts
 * and whether there is ground to lie on are worked out here, from nothing either side could disagree on:
 * the arrow's id, its line and the map.
 */

/**
 * How the flight ended: at the end of its reach, falling onto a floor from above, against a blocker, or struck into a
 * cliff's face (openspec world/cliffs), where it sticks and is lost.
 */
export type SpentArrowEnd = "reach" | "fell" | "blocked" | "face"

export interface SpentArrowLanding {
    /** The arrow that flew: its id seeds the pose, `flags.fromPack` says it was paid for. */
    bullet: Entity
    shooterId: string
    /** Where the flight ended, in this room's coordinates. */
    at: Coordinate
    /** The way it flew, radians. */
    heading: number
    end: SpentArrowEnd
    /** "fell" only: how steeply it came down, degrees. */
    pitch?: number
    /** "fell" only: the dirt puff's size (the cross-floor drop's own measure). */
    strikeStrength?: number
    /** "face" only: how far up the face it struck, as its flight was drawn there. */
    lift?: number
}

/** The share of spent arrows that last; the rest break where they come down. */
export const SPENT_ARROW_SURVIVAL = 0.8

// The pose table (design decision 4). Ground shots have no height in the simulation, so their slant
// is chosen rather than computed: a 160px shot from the shoulder comes in low.
const REACH_PITCH = 22, REACH_PITCH_JITTER = 4, REACH_HEADING_JITTER = 6
const FELL_PITCH_MIN = 22, FELL_PITCH_MAX = 80
// A blocked arrow glances back off the face toward the archer, turned 25-65 degrees either way.
const GLANCE_MIN = 25, GLANCE_MAX = 65
/** How far back from a blocker's face a glanced arrow comes to rest. */
export const GLANCE_STEP_BACK_PX = 6

const radians = (degrees: number) => degrees * Math.PI / 180

/**
 * Three stable numbers in [0, 1) from an arrow's id: two pose it, the third decides whether it lasts. AVALANCHED: ids minted together share all but a
 * few characters, and a plain polynomial hash would pose a volley as one stamped arrow.
 */
export const spentArrowJitter = (id: string): [number, number, number] => {
    let h = 0x811c9dc5
    for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 0x01000193)
    const next = () => {
        h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b)
        h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35)
        h ^= h >>> 16
        return (h >>> 0) / 4294967296
    }
    return [next(), next(), next()]
}

/** Whether this arrow lasts, or breaks where it comes down: decided once, by its id. */
export const spentArrowLasts = (id: string): boolean => spentArrowJitter(id || "")[2] < SPENT_ARROW_SURVIVAL

/** Where and how the arrow lies, before any check of the ground. */
export const spentArrowPose = (landing: SpentArrowLanding): { at: Coordinate; heading: number; pitch: number } => {
    const [a, b] = spentArrowJitter(landing.bullet.entityId || "")
    const { at, heading } = landing
    // Struck into a cliff's face: it stays as it flew, its head in the rock.
    if (landing.end === "face") return { at: { ...at }, heading, pitch: 0 }
    if (landing.end === "blocked") {
        const glance = radians(GLANCE_MIN + (GLANCE_MAX - GLANCE_MIN) * a) * (b < .5 ? -1 : 1)
        return { at: { x: at.x - Math.cos(heading) * GLANCE_STEP_BACK_PX, y: at.y - Math.sin(heading) * GLANCE_STEP_BACK_PX },
            heading: heading + Math.PI + glance, pitch: 0 }
    }
    if (landing.end === "fell") {
        const pitch = Math.max(FELL_PITCH_MIN, Math.min(FELL_PITCH_MAX, landing.pitch ?? FELL_PITCH_MIN))
        return { at: { ...at }, heading, pitch: Math.round(pitch) }
    }
    return { at: { ...at }, heading: heading + radians((a * 2 - 1) * REACH_HEADING_JITTER),
        pitch: Math.round(REACH_PITCH + (b * 2 - 1) * REACH_PITCH_JITTER) }
}

// The march blockedStop takes along a flight's line. Fine enough that its stop is at the blocker's face, not a
// stride short of it.
const STOP_STEP_PX = 2

/**
 * Where a flight stopped by a blocker came to rest, the same on both sides: the flight's line walked from
 * the bow in fixed small steps, through the mover the flight itself moves through, to the first step that
 * goes nowhere. Not where the flight's own last step left it. That step is the stepper's -- a frame on the
 * shooter's client, a pass on the server -- so the two would put the arrow down up to a stride apart.
 * Undefined if this map lets the whole reach through: the flight was stopped by something the map here
 * does not have, and only where it really stopped will do. Stopped by a cliff's face, `face` is how far up it struck.
 */
export const blockedStop = (
    flight: Pick<Entity, "origin" | "speed2" | "maxDistance" | "dimensions" | "spriteId"> & { projectileTarget?: ShotAim },
    map: TileMapMetaProvider | undefined,
): (Coordinate & { face?: number }) | undefined => {
    const { origin, speed2, maxDistance } = flight
    const speed = Math.hypot(speed2?.x ?? 0, speed2?.y ?? 0)
    if (!map || !origin || maxDistance === undefined || !speed || !flight.dimensions) return undefined
    const probe = new Entity()
    probe.entityType = "bullet"
    probe.location = { x: origin.x, y: origin.y }
    probe.dimensions = { ...flight.dimensions }
    const mover = shotMover(map, flight)
    const levels = cliffLevelsOf(map)
    const step = { x: speed2.x / speed * STOP_STEP_PX, y: speed2.y / speed * STOP_STEP_PX }
    const result = ApplyInputResultPool.instance.checkout()
    try {
        for (let walked = 0; walked <= maxDistance; walked += STOP_STEP_PX) {
            probe.applyInput(undefined, STOP_STEP_PX / speed, speed2, result.data, mover, false)
            if (result.data.movedX || result.data.movedY) continue
            const at = { x: probe.location.x, y: probe.location.y }
            const face = levels ? shotFaceStrike(levels, flight, at, step) : undefined
            return face === undefined ? at : { ...at, face }
        }
        return undefined
    } finally { ApplyInputResultPool.instance.release(result) }
}

/** The mover a shot flies through (as `blockedStop` walks it): the map's, stopped at a cliff's face it meets low. */
export const shotMover = (map: TileMapMetaProvider, flight: Pick<Entity, "origin" | "speed2" | "maxDistance" | "spriteId"> &
    { projectileTarget?: ShotAim }) =>
    shotCliffMover(map, collisionMover(map, false, false, flight.spriteId === WeaponCharacteristics.bow.bulletSpriteId), flight)

/**
 * Whether a fetchable arrow can lie at this point: inside the map, not in a blocking cell, not in
 * water, and on an upper storey only where there is a floor (or the roof below) to lie on.
 */
export const isOpenGroundForArrow = (map: TileMapMetaProvider | undefined, at: Coordinate): boolean => {
    if (!map || !Number.isFinite(at.x) || !Number.isFinite(at.y)) return false
    const x = Math.floor(at.x / TileSize), y = Math.floor(at.y / TileSize)
    if (x < 0 || y < 0 || x >= map.width() || y >= map.height()) return false
    if (map.isCollisionAt(x, y)) return false
    const water = map.getWaterLayer()?.[x]?.[y]
    if (water && water !== "0") return false
    if ((map.twoStoreyHouse?.floor ?? 0) > 0 &&
        (map.isFloorOpeningAt(x, y) || map.isStairAt(x, y) || map.isUnsupportedFloorAt(x, y))) return false
    return true
}
